<template>
	<v-navigation-drawer
		v-model="isOpen"
		location="right"
		temporary
		width="500"
		class="trial-edit-modal"
	>
		<v-card flat height="100%" class="d-flex flex-column">
			<v-card-title class="d-flex align-center justify-space-between pa-4 flex-shrink-0">
				<span class="text-h6">Edit Trial</span>
				<v-btn icon variant="text" size="small" @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider class="flex-shrink-0" />

			<v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
				<v-form ref="form" v-model="valid" lazy-validation>
					<v-row>
						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.school_name"
								label="School Name*"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.district_name"
								label="District Name*"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.firstname"
								label="First Name*"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.lastname"
								label="Last Name*"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.email"
								label="Email*"
								:rules="[rules.required, rules.email]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model="editedTrial.phone_number"
								label="Phone Number*"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-select
								v-model="editedTrial.timezone"
								label="Timezone*"
								:items="timezones"
								:rules="[rules.required]"
								required
							/>
						</v-col>

						<v-col cols="6">
							<v-text-field
								v-model.number="editedTrial.num_students"
								label="Student Enrollment"
								type="number"
								:rules="[rules.number]"
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.created_tstamp"
								label="Created Date"
								readonly
								disabled
							/>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="editedTrial.trial_id"
								label="Trial ID"
								readonly
								disabled
							/>
						</v-col>

						<!-- Show fulfillment status if trial is fulfilled -->
						<v-col cols="12" v-if="props.trial?.status === 'fullfilled'">
							<v-alert 
								:type="props.trial?.status === 'fullfilled' ? 'success' : 'warning'"
								density="compact"
							>
								<template v-if="props.trial?.status === 'fullfilled'"">
									<v-icon>mdi-check-circle</v-icon>
									Trial is fully fulfilled - matching school record exists
								</template>
								<template v-else>
									<v-icon>mdi-alert</v-icon>
									Trial status is fulfilled but no matching school record found
								</template>
							</v-alert>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>

			<v-divider class="flex-shrink-0" />

			<v-card-actions class="pa-4 flex-shrink-0" style="min-height: 80px">
				<v-spacer />
				<v-btn
					color="grey"
					variant="outlined"
					@click="close"
					:disabled="loading || approving"
				>
					Cancel
				</v-btn>

				<!-- Show approve/reject buttons based on fulfillment status -->
				<template v-if="canShowApproveRejectButtons">
					<v-btn
						color="error"
						variant="outlined"
						@click="rejectTrial"
						:loading="approving"
						:disabled="loading"
					>
						Reject Trial
					</v-btn>
					<v-btn
						color="success"
						variant="flat"
						@click="approveTrial"
						:loading="approving"
						:disabled="loading || !canApproveTrial"
					>
						<v-icon left>mdi-check-circle</v-icon>
						Approve Trial
					</v-btn>
				</template>

				<v-btn
					color="primary"
					variant="flat"
					@click="saveTrial('pending')"
					:loading="loading"
					:disabled="!valid || approving"
				>
					Save Details
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-navigation-drawer>
</template>

<script setup lang="ts">

import { ref, watch, computed, onUpdated } from 'vue';
import { useAlertsStore } from '@/stores/alerts';
import useTrials, { type TrialMembership } from '@/stores/support/trials';
import dayjs from 'dayjs';

const props = defineProps<{
  modelValue: boolean;
  trial: TrialMembership | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const trialsStore = useTrials();
const alertStore = useAlertsStore();

const form = ref();
const valid = ref(false);
const loading = ref(false);
const approving = ref(false);

const editedTrial = ref<Partial<TrialMembership>>({});

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// US Timezones
const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu'
];

const statusOptions = [
  { title: 'Pending', value: 'pending' },
  { title: 'Fulfilled', value: 'fulfilled' },
  { title: 'Expired', value: 'expired' },
  { title: 'Cancelled', value: 'cancelled' },
  { title: 'Rejected', value: 'rejected' }
];

const rules = {
  required: (value: any) => !!value || 'Field is required',
  email: (value: string) => /.+@.+/.test(value) || 'Invalid email address',
  number: (value: any) => !value || (!isNaN(parseFloat(value)) && isFinite(value)) || 'Must be a number'
};


const canApproveTrial = computed(() => {
  if (!props.trial) return false;
  
  return props.trial.status === 'pending' || props.trial.status === 'fullfilled';
});

const canShowApproveRejectButtons = computed(() => {
  if (!props.trial) return false;
  
  // Show buttons if trial can be approved OR if it's pending
  return canApproveTrial.value || props.trial.status === 'pending';
});


// Watch for trial prop changes
watch(() => props.trial, (newTrial) => {
  if (newTrial) {
    editedTrial.value = { ...newTrial };
    // Format the date for display
    if (editedTrial.value.created_tstamp) {
      editedTrial.value.created_tstamp = dayjs(editedTrial.value.created_tstamp).format('MM/DD/YYYY HH:mm');
    }
  }
}, { immediate: true });

const close = () => {
  isOpen.value = false;
  // Reset form after animation completes
  setTimeout(() => {
    editedTrial.value = {};
    if (form.value) {
      form.value.resetValidation();
    }
  }, 300);
};

const saveTrial = async (trialStatus: TrialMembership['status'] | any) => {
	if (!form.value) return;

	const { valid: isValid } = await form.value.validate();
	if (!isValid || !props.trial) return;

	loading.value = true;

	console.log('Saving trial with status:', trialStatus);
	if (trialStatus) {
		editedTrial.value.status = trialStatus;
	} else {
		// If no status is provided, default to 'pending'
		editedTrial.value.status = 'pending';
	}
	try {
		// Prepare the update payload from the form data
		const updateData = {
			school_name: editedTrial.value.school_name,
			district_name: editedTrial.value.district_name,
			firstname: editedTrial.value.firstname,
			lastname: editedTrial.value.lastname,
			email: editedTrial.value.email,
			phone_number: editedTrial.value.phone_number,
			username: editedTrial.value.username,
			timezone: editedTrial.value.timezone,
			status: editedTrial.value.status,
			num_students: editedTrial.value.num_students
		};

		console.log('Update payload:', updateData);

		const result = await trialsStore.updateTrial(props.trial.trial_id, updateData);

		if (result.status) {
			alertStore.addAlert('success', 'Trial updated successfully', 5);
			console.log('Update completed with payload:', updateData);
			console.log('Updated trial:', result.data);
			close();
		} else {
			alertStore.addAlert('error', result.message || 'Failed to update trial', 10);
		}
	} catch (error) {
		console.error('Error updating trial:', error);
		alertStore.addAlert('error', 'An error occurred while updating the trial', 10);
	} finally {
		loading.value = false;
	}
};

const approveTrial = async () => {
  saveTrial('fullfilled');
};

const rejectTrial = async () => {
  saveTrial('rejected');
};
</script>

<style scoped lang="scss">
.trial-edit-modal {
	.v-card-title {
		background-color: #f5f5f5;
		border-bottom: 1px solid #e0e0e0;
	}

	.v-card-actions {
		background-color: #f5f5f5;
		border-top: 1px solid #e0e0e0;
	}
}
</style>
